import { reactive } from 'vue';
import { UsersService } from '@/api';
import { Debounce } from '@/common/debounce-decorator';

export const SmallFilterItemTypes = {
  CameraGroups: 'camera-groups',
  Cameras: 'cameras',
  CardsCars: 'cards_cars',
  CardsHumans: 'cards_humans',
  CaseClusters: 'case-clusters',
  CaseParticipants: 'case-participants',
  ClustersBodies: 'clusters_bodies',
  ClustersCars: 'clusters_cars',
  ClustersFaces: 'clusters_faces',
  Counters: 'counters',
  CountersRecords: 'counters-records',
  EpisodesCars: 'episodes_cars',
  EpisodesHumans: 'episodes_humans',
  EventsBodies: 'events_bodies',
  EventsCars: 'events_cars',
  EventsFaces: 'events_faces',
  ExternalDetectors: 'external_detectors',
  ExternalVms: 'external-vms',
  Groups: 'groups',
  Searches: 'searches',
  Users: 'users',
  Videos: 'videos',
  WatchLists: 'watch-lists',
  Webhooks: 'hooks',
  AuditLogs: 'audit-logs',
  Blacklists: 'device-blacklist-records',
  Sessions: 'sessions',
  Analytics: 'analytics',
  Lines: 'lines',
  RemoteMonitoringEvents: 'puppeteer_remote-monitoring_events',
  Cases: 'cases',
  Alarms: 'alerts'
};

export type Filter = {
  source: string;
  filters: string[];
};

const EventsSmallFilters = ['acknowledged', 'no_match', 'no_face_match', 'no_body_match', 'no_car_match', 'limit'];
const ClusterSmallFilters = ['has_card', 'matched_lists', 'limit'];
const VideoSmallFilters = ['name_contains', 'camera_groups'];
const AuditLogs = ['object_type', 'action_type'];

/* @todo: Move this section to modules config */
const DefaultSmallFiltersMap: Record<string, string[]> = {
  [SmallFilterItemTypes.Analytics]: ['analytics'],
  [SmallFilterItemTypes.AuditLogs]: AuditLogs,
  [SmallFilterItemTypes.Blacklists]: ['uuid_contains', 'reason_contains'],
  [SmallFilterItemTypes.Cameras]: VideoSmallFilters,
  [SmallFilterItemTypes.CaseClusters]: ['is_case_participant', 'acknowledged'], // @todor fix after rename filter
  [SmallFilterItemTypes.CaseParticipants]: ['name_contains', 'role_in'],
  [SmallFilterItemTypes.CardsCars]: ['has_car_objects', 'license_plate_number_contains', 'limit'],
  [SmallFilterItemTypes.CardsHumans]: ['has_face_objects', 'has_body_objects', 'name_contains', 'limit'],
  [SmallFilterItemTypes.ClustersBodies]: ClusterSmallFilters,
  [SmallFilterItemTypes.ClustersCars]: ClusterSmallFilters,
  [SmallFilterItemTypes.ClustersFaces]: ClusterSmallFilters,
  [SmallFilterItemTypes.Counters]: ['id_in'],
  [SmallFilterItemTypes.CountersRecords]: [],
  [SmallFilterItemTypes.EpisodesCars]: EventsSmallFilters,
  [SmallFilterItemTypes.EpisodesHumans]: EventsSmallFilters,
  [SmallFilterItemTypes.EventsBodies]: EventsSmallFilters,
  [SmallFilterItemTypes.EventsCars]: EventsSmallFilters,
  [SmallFilterItemTypes.EventsFaces]: EventsSmallFilters,
  [SmallFilterItemTypes.ExternalDetectors]: VideoSmallFilters,
  [SmallFilterItemTypes.Lines]: ['name_contains', 'id_in'],
  [SmallFilterItemTypes.Sessions]: ['user_name_in', 'mobile', 'status'],
  [SmallFilterItemTypes.Videos]: VideoSmallFilters,
  [SmallFilterItemTypes.RemoteMonitoringEvents]: ['card', 'watch_lists'],
  [SmallFilterItemTypes.Cases]: ['has_unacknowledged_case_clusters'],
  [SmallFilterItemTypes.Alarms]: ['status'],
  ['alerts_filter']: ['status']
};

export class SmallFilterManagerModule {
  static Name = 'SmallFilterManagerModule';
  static StorageKey = 'smallFilters';

  public readonly items: Record<string, string[]> = { ...DefaultSmallFiltersMap };
  loaded = false;

  async setFiltersBySource(source: string, items: string[], save: boolean = false) {
    this.items[source] = items;
    if (save) await this.saveUserFilters();
  }

  getFiltersBySource(source: string): string[] {
    return this.items[source] || DefaultSmallFiltersMap[source] || [];
  }

  async loadUserFilters() {
    try {
      const smallFiltersData = await UsersService.usersMeDataRetrieve(SmallFilterManagerModule.StorageKey);
      const savedFields = JSON.parse(smallFiltersData?.value);
      Object.assign(this.items, savedFields);
    } catch (e) {
      console.warn('[users.service]:getFilters error', e);
    } finally {
      this.loaded = true;
    }
  }

  @Debounce(1000)
  async saveUserFilters() {
    try {
      await UsersService.usersMeDataUpdate('smallFilters', { key: SmallFilterManagerModule.StorageKey, value: JSON.stringify(this.items) ?? '' });
    } catch (e) {
      console.warn('[users.service]:saveFilters error', e);
    }
  }
}

export const smallFilterManagerModule = reactive(new SmallFilterManagerModule());
